import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2, RefreshCw, Zap, Shield, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const AIWorkflowAutomation = () => {
  const scrollToDemo = () => {
    window.location.href = '/?scrollTo=demo';
  };

  return (
    <Layout>
      <SEOHead
        title="AI Workflow Automation for Revenue Teams | NoQ5"
        description="Automate your entire revenue workflow with AI. NoQ5 connects meetings, emails, CRM, and Slack into one intelligent pipeline. No more manual data entry or missed follow-ups."
        keywords="AI workflow automation, workflow AI sales, automated sales workflow, revenue workflow automation, AI sales operations, intelligent workflow CRM, sales process automation AI, RevOps automation"
        canonicalUrl="https://noq5.ai/ai-workflow-automation"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "AI Workflow Automation for Revenue Teams",
          "description": "Automate your entire revenue workflow with AI. NoQ5 connects meetings, emails, CRM, and Slack into one intelligent pipeline.",
          "url": "https://noq5.ai/ai-workflow-automation",
          "publisher": { "@type": "Organization", "name": "NoQ5.ai", "url": "https://noq5.ai" }
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is AI workflow automation for sales?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "AI workflow automation for sales uses artificial intelligence to connect and automate the end-to-end revenue process. Instead of manual steps between meetings, CRM updates, follow-ups, and reporting, AI handles the entire flow autonomously. NoQ5 does this across HubSpot, Salesforce, and Pipedrive."
              }
            },
            {
              "@type": "Question",
              "name": "How does NoQ5 automate revenue workflows?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "NoQ5 connects to your meetings (Google Meet, Zoom), email (Gmail, Outlook), CRM (HubSpot, Salesforce, Pipedrive), and messaging (Slack). It automatically captures deal activity, updates CRM records, generates meeting summaries, scores pipeline health, sends follow-up reminders, and delivers weekly digests to leadership."
              }
            }
          ]
        }]}
      />

      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/8 border border-primary/15 mb-6">
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground/80">End-to-End Revenue Automation</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              AI Workflow Automation<br />for Revenue Teams
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Stop stitching together Zapier workflows and manual processes. NoQ5 is the intelligent automation layer that connects every part of your revenue operation into one self-running system.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-xl" onClick={scrollToDemo}>
              Talk to Sales <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>

          {/* The Workflow */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">How NoQ5 Automates Your Revenue Workflow</h2>
            <div className="space-y-6 max-w-3xl mx-auto">
              {[
                { step: '1', title: 'Meeting happens', desc: 'Rep joins a call on Google Meet, Zoom, or Teams. NoQ5 joins automatically, transcribes, and extracts key insights.' },
                { step: '2', title: 'CRM updates itself', desc: 'Deal stage, next steps, competitor mentions, and contact info flow directly into HubSpot, Salesforce, or Pipedrive. Zero manual entry.' },
                { step: '3', title: 'Pipeline scores refresh', desc: 'Every deal gets an updated health score based on buyer engagement, email velocity, and multi-threading depth.' },
                { step: '4', title: 'Alerts fire', desc: 'If a deal is going dark, a follow-up is overdue, or a competitor was mentioned, the right person gets notified via Slack or email.' },
                { step: '5', title: 'Forecast updates', desc: 'Evidence-based forecast recalculates in real-time. No Sunday night pipeline scrubs needed.' },
                { step: '6', title: 'Monday Digest arrives', desc: 'CRO and managers get a weekly summary of pipeline movement, risks, wins, and key metrics delivered to their inbox.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  viewport={{ once: true }}
                  className="flex gap-4 items-start p-5 bg-card border border-border rounded-xl"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-primary font-bold">{item.step}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                    <p className="text-muted-foreground text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-foreground text-center mb-8">Why Revenue Teams Choose NoQ5</h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[
                'Eliminate hours of manual CRM data entry every week',
                'Higher CRM data completeness without nagging reps',
                'Earlier visibility into pipeline risks with proactive alerts',
                'Faster ramp for new reps with full deal context transfer',
                'Forecast based on actual signals, not rep optimism',
                'GDPR-compliant, EU-hosted, with full audit trails',
              ].map((benefit, i) => (
                <div key={i} className="flex items-start gap-3 p-4">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-foreground">{benefit}</span>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {[
                { q: 'What is AI workflow automation for sales?', a: 'AI workflow automation uses artificial intelligence to connect and run your revenue operations end-to-end. NoQ5 handles meeting capture, CRM updates, pipeline scoring, forecasting, alerts, and outreach without manual intervention or complex workflow builders.' },
                { q: 'How is this different from Zapier or Make?', a: 'Zapier and Make are rule-based connectors. You build "if X then Y" workflows manually. NoQ5 is an intelligent agent that understands the context of your deals and takes the right actions autonomously. No workflow building required.' },
                { q: 'What integrations does NoQ5 support?', a: 'NoQ5 integrates with HubSpot, Salesforce, Pipedrive, Google Workspace (Calendar, Gmail, Meet), Slack, Zoom, and Microsoft 365 (coming soon). LinkedIn integration is also on the roadmap.' },
                { q: 'How quickly can we get started?', a: 'Under 5 minutes. Connect your CRM and communication tools via OAuth. NoQ5 starts processing immediately with no configuration needed.' },
              ].map((faq, i) => (
                <div key={i} className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal Links */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-foreground text-center mb-8">Explore More</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/agentic-ai-sales" className="p-4 bg-card border border-border rounded-xl hover:border-primary/40 transition-colors text-center">
                <h3 className="font-semibold text-foreground mb-1">Agentic AI for Sales</h3>
                <p className="text-sm text-muted-foreground">Autonomous revenue operations</p>
              </Link>
              <Link to="/use-cases/crm-automation" className="p-4 bg-card border border-border rounded-xl hover:border-primary/40 transition-colors text-center">
                <h3 className="font-semibold text-foreground mb-1">CRM Automation</h3>
                <p className="text-sm text-muted-foreground">Zero-touch CRM updates</p>
              </Link>
              <Link to="/product/command-center" className="p-4 bg-card border border-border rounded-xl hover:border-primary/40 transition-colors text-center">
                <h3 className="font-semibold text-foreground mb-1">Command Center</h3>
                <p className="text-sm text-muted-foreground">CRO pipeline dashboard</p>
              </Link>
            </div>
          </section>

          {/* CTA */}
          <div className="text-center p-12 bg-primary/5 border border-primary/20 rounded-2xl">
            <h2 className="text-3xl font-bold text-foreground mb-4">Automate Your Revenue Workflow Today</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Sales teams are saving 8+ hours per rep per week with NoQ5 intelligent automation.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-xl" onClick={scrollToDemo}>
              Talk to Sales <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AIWorkflowAutomation;
